import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { useState } from "react";
import { CreateOrganizationFlow } from "./organization/CreateOrganizationFlow";

export type Organization = {
  id: string;
  name: string;
  slug: string;
  currency?: string;
  user_role?: string; // viewer, editor, or admin
};

export default function OrganizationSelector({
  value,
  onChange,
  orgs,
  onOrganizationCreated,
}: {
  value: Organization | null;
  onChange: (org: Organization) => void;
  orgs: Organization[];
  onOrganizationCreated?: (newOrg: Organization) => void;
}) {
  const [addOpen, setAddOpen] = useState(false);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <FormControl
        size="small"
        sx={{ minWidth: 180, flex: 1 }}
        variant="outlined"
      >
        <InputLabel sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
          Organization
        </InputLabel>
        <Select
          value={value ? value.id : ""}
          label="Organization"
          onChange={(e) => {
            const org = orgs.find((o) => o.id === e.target.value);
            if (org) onChange(org);
          }}
          sx={{
            background: "rgba(0, 0, 0, 0.2)",
            color: "#fff",
            fontWeight: 500,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255, 255, 255, 0.2)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255, 255, 255, 0.4)",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#6366f1",
            },
            "& .MuiSvgIcon-root": {
              color: "rgba(255, 255, 255, 0.7)",
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: "#1f2937",
                color: "#fff",
                border: "1px solid rgba(255, 255, 255, 0.1)",
              },
            },
          }}
        >
          {orgs.map((org) => (
            <MenuItem
              key={org.id}
              value={org.id}
              sx={{
                color: "#fff",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              {org.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        variant="outlined"
        size="small"
        onClick={() => setAddOpen(true)}
        sx={{
          minWidth: 32,
          width: 32,
          height: 32,
          p: 0,
          color: "rgba(255, 255, 255, 0.7)",
          borderColor: "rgba(255, 255, 255, 0.2)",
          "&:hover": {
            borderColor: "rgba(255, 255, 255, 0.4)",
            backgroundColor: "rgba(255, 255, 255, 0.05)",
          },
        }}
      >
        +
      </Button>

      <CreateOrganizationFlow
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onOrganizationCreated={(newOrg) => {
          setAddOpen(false);
          if (onOrganizationCreated) {
            onOrganizationCreated(newOrg);
          }
        }}
      />
    </Box>
  );
}
